import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiRequest } from '../utils/api';

type ExchangePlatform = 'traodoisub' | 'tuongtaccheo';
type AccountAction = 'login' | 'check-cookie' | 'update-coin';
type ActionLogStatus = 'running' | 'success' | 'failed';

interface ExchangeAccount {
  id: number;
  username: string;
  password: string;
  cookie: string | null;
  access_token: string | null;
  api_key?: string | null;
  coin: number;
  coin_die: number;
  malucsub?: number;
  maluclikepage?: number;
  status: string | null;
  note: string | null;
  group?: string | null;
  group_name?: string | null;
  is_live: boolean;
  created: string | null;
}

interface ExchangeStats {
  totalAccounts: number;
  totalCoin: number;
  totalCoinDie: number;
  totalMalucsub?: number;
  totalMaluclikepage?: number;
  uniqueGroups: string[];
}

interface ExchangeAccountManagerProps {
  platform: ExchangePlatform;
  title: string;
  description: string;
  showTtcMetrics?: boolean;
}

interface ActionLogEntry {
  id: number;
  username?: string;
  status: ActionLogStatus;
  message: string;
  startedAt: number;
  finishedAt?: number;
}

interface ActionProgress {
  action: AccountAction;
  label: string;
  active: boolean;
  total: number;
  completed: number;
  success: number;
  failed: number;
  startedAt: number;
  finishedAt?: number;
  currentId?: number;
  currentUsername?: string;
  logs: ActionLogEntry[];
}

const LIMIT = 50;
const ACTION_REQUEST_TIMEOUT_MS = 45000;
const MAX_PROGRESS_LOGS = 14;

const actionConfig: Record<AccountAction, { endpoint: string; label: string; running: string }> = {
  login: { endpoint: 'login', label: 'Đăng nhập', running: 'Đang đăng nhập' },
  'check-cookie': { endpoint: 'check-cookie', label: 'Kiểm tra cookie', running: 'Đang kiểm tra cookie' },
  'update-coin': { endpoint: 'update-coin', label: 'Cập nhật xu/mã lực', running: 'Đang cập nhật xu/mã lực' },
};

const statusLabels: Record<string, string> = {
  imported: 'Đã import',
  login_success: 'Đăng nhập OK',
  login_fail: 'Đăng nhập lỗi',
  cookie_live: 'Cookie live',
  cookie_dead: 'Cookie die',
  coin_updated: 'Đã cập nhật xu',
  token_invalid: 'Token lỗi',
  active: 'Hoạt động',
  inactive: 'Tạm tắt',
  die: 'Die',
};

const statusColors: Record<string, { bg: string; text: string }> = {
  imported: { bg: '#eef2ff', text: '#3730a3' },
  login_success: { bg: '#dcfce7', text: '#166534' },
  cookie_live: { bg: '#dcfce7', text: '#166534' },
  coin_updated: { bg: '#dbeafe', text: '#1d4ed8' },
  login_fail: { bg: '#fee2e2', text: '#991b1b' },
  cookie_dead: { bg: '#fee2e2', text: '#991b1b' },
  token_invalid: { bg: '#fee2e2', text: '#991b1b' },
  active: { bg: '#dcfce7', text: '#166534' },
  inactive: { bg: '#fef3c7', text: '#92400e' },
  die: { bg: '#fee2e2', text: '#991b1b' },
};

const emptyStats: ExchangeStats = {
  totalAccounts: 0,
  totalCoin: 0,
  totalCoinDie: 0,
  totalMalucsub: 0,
  totalMaluclikepage: 0,
  uniqueGroups: [],
};

function formatNumber(value: number | null | undefined): string {
  return Number(value || 0).toLocaleString('vi-VN');
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('vi-VN');
}

function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(seconds / 60);
  const remain = seconds % 60;
  return minutes > 0 ? `${minutes}m ${remain}s` : `${remain}s`;
}

function groupOf(account: ExchangeAccount): string | null {
  return account.group ?? account.group_name ?? null;
}

async function readJson(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

const ExchangeAccountManager: React.FC<ExchangeAccountManagerProps> = ({ platform, title, description, showTtcMetrics = false }) => {
  const apiBase = `/api/${platform}`;
  const [accounts, setAccounts] = useState<ExchangeAccount[]>([]);
  const [stats, setStats] = useState<ExchangeStats>(emptyStats);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionProgress, setActionProgress] = useState<ActionProgress | null>(null);
  const [clockTick, setClockTick] = useState(Date.now());

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const [maxCoinFilter, setMaxCoinFilter] = useState('');
  const [debouncedMaxCoin, setDebouncedMaxCoin] = useState('');
  const [coinSort, setCoinSort] = useState('none');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importGroup, setImportGroup] = useState('');

  const actionAbortRef = useRef<AbortController | null>(null);
  const cancelActionRef = useRef(false);

  const selectedList = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const accountLookup = useMemo(() => new Map(accounts.map(account => [account.id, account])), [accounts]);
  const busy = loading || actionLoading !== null || Boolean(actionProgress?.active);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setDebouncedMaxCoin(maxCoinFilter);
      setPage(1);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [searchQuery, maxCoinFilter]);

  useEffect(() => {
    setPage(1);
  }, [filterGroup, coinSort]);

  useEffect(() => {
    if (!actionProgress?.active) return;
    const timer = window.setInterval(() => setClockTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [actionProgress?.active]);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(LIMIT),
        search: debouncedSearch,
        group: filterGroup,
        maxCoin: debouncedMaxCoin,
        coinSort,
      });
      const response = await apiRequest(`${apiBase}?${params.toString()}`, { method: 'GET' });
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data.error || 'Không thể tải danh sách tài khoản');
      }
      setAccounts(data.accounts || []);
      setStats(data.stats || emptyStats);
      setTotalPages(data.pagination?.totalPages || 1);
      setSelectedIds(new Set());
    } catch (error: any) {
      alert(error.message || 'Không thể tải danh sách tài khoản');
    } finally {
      setLoading(false);
    }
  }, [apiBase, page, debouncedSearch, filterGroup, debouncedMaxCoin, coinSort]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const requestJsonWithTimeout = async (url: string, options: RequestInit): Promise<any> => {
    const controller = new AbortController();
    actionAbortRef.current = controller;
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, ACTION_REQUEST_TIMEOUT_MS);

    try {
      const response = await apiRequest(url, { ...options, signal: controller.signal });
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data.error || data.message || `HTTP ${response.status}`);
      }
      return data;
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        throw new Error(timedOut ? `Timeout sau ${Math.round(ACTION_REQUEST_TIMEOUT_MS / 1000)} giây. Thường do proxy hoặc site đích không phản hồi.` : 'Đã hủy thao tác');
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
      if (actionAbortRef.current === controller) {
        actionAbortRef.current = null;
      }
    }
  };

  const toggleSelectAll = () => {
    if (accounts.length > 0 && selectedIds.size === accounts.length) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(accounts.map(account => account.id)));
  };

  const toggleSelectOne = (id: number) => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateRunningLog = (id: number, updater: (log: ActionLogEntry) => ActionLogEntry) => {
    setActionProgress(previous => {
      if (!previous) return previous;
      return {
        ...previous,
        logs: previous.logs.map(log => (log.id === id && log.status === 'running' ? updater(log) : log)),
      };
    });
  };

  const finishProgress = (cancelled = false) => {
    setActionProgress(previous => previous ? {
      ...previous,
      active: false,
      finishedAt: Date.now(),
      currentId: undefined,
      currentUsername: cancelled ? 'Đã hủy thao tác' : undefined,
    } : previous);
    cancelActionRef.current = false;
  };

  const runAccountAction = async (action: AccountAction, ids: number[]) => {
    if (ids.length === 0 || actionProgress?.active) return;
    const config = actionConfig[action];
    const uniqueIds = Array.from(new Set(ids));
    const startedAt = Date.now();

    cancelActionRef.current = false;
    setClockTick(startedAt);
    setActionProgress({
      action,
      label: config.label,
      active: true,
      total: uniqueIds.length,
      completed: 0,
      success: 0,
      failed: 0,
      startedAt,
      logs: [],
    });

    for (const id of uniqueIds) {
      if (cancelActionRef.current) break;
      const account = accountLookup.get(id);
      const username = account?.username;
      const logStartedAt = Date.now();
      const runningLog: ActionLogEntry = { id, username, status: 'running', message: 'Đang gửi request...', startedAt: logStartedAt };

      setActionProgress(previous => previous ? {
        ...previous,
        currentId: id,
        currentUsername: username,
        logs: [
          runningLog,
          ...previous.logs,
        ].slice(0, MAX_PROGRESS_LOGS),
      } : previous);

      try {
        const data = await requestJsonWithTimeout(`${apiBase}/${config.endpoint}`, {
          method: 'POST',
          body: JSON.stringify({ ids: [id] }),
        });
        const result = data.results?.[0];
        const ok = Boolean(result?.success);
        const message = result?.note || data.message || (ok ? `${config.label} thành công` : `${config.label} thất bại`);

        updateRunningLog(id, log => ({
          ...log,
          status: ok ? 'success' : 'failed',
          message,
          finishedAt: Date.now(),
        }));
        setActionProgress(previous => previous ? {
          ...previous,
          completed: previous.completed + 1,
          success: previous.success + (ok ? 1 : 0),
          failed: previous.failed + (ok ? 0 : 1),
        } : previous);
      } catch (error: any) {
        const message = error.message || `${config.label} thất bại`;
        updateRunningLog(id, log => ({
          ...log,
          status: 'failed',
          message,
          finishedAt: Date.now(),
        }));
        setActionProgress(previous => previous ? {
          ...previous,
          completed: previous.completed + 1,
          failed: previous.failed + 1,
        } : previous);
        if (cancelActionRef.current) break;
      }
    }

    finishProgress(cancelActionRef.current);
    await loadAccounts();
  };

  const cancelRunningAction = () => {
    cancelActionRef.current = true;
    actionAbortRef.current?.abort();
  };

  const handleImport = async () => {
    if (!importText.trim()) {
      alert('Vui lòng nhập danh sách tài khoản');
      return;
    }
    try {
      setActionLoading('import');
      const response = await apiRequest(`${apiBase}/import`, {
        method: 'POST',
        body: JSON.stringify({ accounts: importText, group: importGroup.trim() || null }),
      });
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data.error || 'Import tài khoản thất bại');
      }
      const extra = data.errors?.length ? `\nDòng lỗi đầu tiên: ${data.errors[0]}` : '';
      alert(`${data.message || 'Import tài khoản xong'}${extra}`);
      setShowImportModal(false);
      setImportText('');
      setImportGroup('');
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || 'Import tài khoản thất bại');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = async () => {
    try {
      setActionLoading('export');
      const response = await apiRequest(`${apiBase}/export`, { method: 'GET' });
      if (!response.ok) {
        const data = await readJson(response);
        throw new Error(data.error || 'Export tài khoản thất bại');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      anchor.href = url;
      anchor.download = `${platform}-accounts-${date}.txt`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error: any) {
      alert(error.message || 'Export tài khoản thất bại');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteSingle = async (id: number, username: string) => {
    if (!window.confirm(`Bạn có chắc muốn xóa tài khoản "${username}"?`)) return;
    try {
      setActionLoading(`delete:${id}`);
      const response = await apiRequest(`${apiBase}/${id}`, { method: 'DELETE' });
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data.error || 'Xóa tài khoản thất bại');
      }
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || 'Xóa tài khoản thất bại');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedList.length === 0) return;
    if (!window.confirm(`Bạn có chắc muốn xóa ${selectedList.length} tài khoản đã chọn?`)) return;
    try {
      setActionLoading('bulk-delete');
      const response = await apiRequest(`${apiBase}/bulk-delete`, {
        method: 'POST',
        body: JSON.stringify({ ids: selectedList }),
      });
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data.error || 'Xóa hàng loạt thất bại');
      }
      alert(data.message || 'Đã xóa tài khoản đã chọn');
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || 'Xóa hàng loạt thất bại');
    } finally {
      setActionLoading(null);
    }
  };

  const renderStatusBadge = (status: string | null) => {
    const key = status || 'unknown';
    const colors = statusColors[key] || { bg: '#f3f4f6', text: '#374151' };
    return (
      <span style={{ ...badgeStyle, backgroundColor: colors.bg, color: colors.text }}>
        {status ? statusLabels[status] || status : 'Chưa kiểm tra'}
      </span>
    );
  };

  const renderLiveBadge = (account: ExchangeAccount) => {
    const deadStatuses = new Set(['login_fail', 'cookie_dead', 'token_invalid', 'die']);
    if (account.is_live) {
      return <span style={{ ...badgeStyle, backgroundColor: '#dcfce7', color: '#166534' }}>Live</span>;
    }
    if (deadStatuses.has(account.status || '')) {
      return <span style={{ ...badgeStyle, backgroundColor: '#fee2e2', color: '#991b1b' }}>Die</span>;
    }
    return <span style={{ ...badgeStyle, backgroundColor: '#f3f4f6', color: '#4b5563' }}>Chưa kiểm tra</span>;
  };

  const renderYesNo = (value: string | null | undefined) => (
    <span style={{ ...badgeStyle, backgroundColor: value ? '#dcfce7' : '#f3f4f6', color: value ? '#166534' : '#4b5563' }}>
      {value ? 'Có' : 'Chưa'}
    </span>
  );

  const selectedActions = selectedList.length > 0 && (
    <div style={selectedBarStyle}>
      <span style={{ fontWeight: 700, color: '#1f2937' }}>Đã chọn {selectedList.length} tài khoản</span>
      <button style={secondaryButtonStyle} disabled={busy} onClick={() => runAccountAction('login', selectedList)}>Đăng nhập</button>
      <button style={secondaryButtonStyle} disabled={busy} onClick={() => runAccountAction('check-cookie', selectedList)}>Kiểm tra cookie</button>
      <button style={secondaryButtonStyle} disabled={busy} onClick={() => runAccountAction('update-coin', selectedList)}>Cập nhật xu/mã lực</button>
      <button style={dangerButtonStyle} disabled={busy} onClick={handleBulkDelete}>Xóa đã chọn</button>
    </div>
  );

  return (
    <div style={{ padding: '0', maxWidth: '100%', overflow: 'hidden' }}>
      <div style={pageHeaderStyle}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 800, color: '#111827' }}>{title}</h1>
          <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: '15px' }}>{description}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button style={secondaryButtonStyle} disabled={busy} onClick={loadAccounts}>Tải lại</button>
          <button style={secondaryButtonStyle} disabled={busy} onClick={handleExport}>
            {actionLoading === 'export' ? 'Đang export...' : 'Export TXT'}
          </button>
          <button style={primaryButtonStyle} disabled={busy} onClick={() => setShowImportModal(true)}>Import account</button>
        </div>
      </div>

      <div style={statsGridStyle}>
        <StatCard label="Tổng tài khoản" value={formatNumber(stats.totalAccounts)} />
        <StatCard label="Tổng xu" value={formatNumber(stats.totalCoin)} />
        <StatCard label="Tổng xu die" value={formatNumber(stats.totalCoinDie)} />
        {showTtcMetrics && <StatCard label="Tổng mã lực sub" value={formatNumber(stats.totalMalucsub)} />}
        {showTtcMetrics && <StatCard label="Tổng mã lực like page" value={formatNumber(stats.totalMaluclikepage)} />}
      </div>

      <div style={toolbarStyle}>
        <input
          type="text"
          placeholder="Tìm username hoặc ghi chú..."
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
          style={{ ...inputStyle, minWidth: 260, flex: '1 1 260px' }}
        />
        <select value={filterGroup} onChange={event => setFilterGroup(event.target.value)} style={inputStyle}>
          <option value="all">Tất cả nhóm</option>
          {stats.uniqueGroups?.map(group => <option key={group} value={group}>{group}</option>)}
        </select>
        <input
          type="number"
          placeholder="Coin tối đa"
          value={maxCoinFilter}
          onChange={event => setMaxCoinFilter(event.target.value)}
          style={{ ...inputStyle, width: 140 }}
        />
        <select value={coinSort} onChange={event => setCoinSort(event.target.value)} style={inputStyle}>
          <option value="none">Sắp xếp coin</option>
          <option value="asc">Coin tăng dần</option>
          <option value="desc">Coin giảm dần</option>
        </select>
      </div>

      {selectedActions}

      <div style={{ marginBottom: 10, color: '#6b7280', fontSize: 13 }}>
        Trang {page}/{totalPages} - đang hiển thị {accounts.length}/{formatNumber(stats.totalAccounts)} tài khoản
      </div>

      <div style={tableShellStyle}>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table style={{ ...tableStyle, minWidth: showTtcMetrics ? 1480 : 1260 }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ ...thStyle, width: 46, textAlign: 'center' }}>
                  <input type="checkbox" checked={accounts.length > 0 && selectedIds.size === accounts.length} onChange={toggleSelectAll} />
                </th>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Username</th>
                <th style={thStyle}>Nhóm</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Coin</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Coin die</th>
                {showTtcMetrics && <th style={{ ...thStyle, textAlign: 'right' }}>Mã lực sub</th>}
                {showTtcMetrics && <th style={{ ...thStyle, textAlign: 'right' }}>ML like page</th>}
                <th style={{ ...thStyle, textAlign: 'center' }}>Cookie</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Token</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Trạng thái</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Live</th>
                <th style={thStyle}>Ghi chú</th>
                <th style={thStyle}>Ngày tạo</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={showTtcMetrics ? 15 : 13} style={emptyCellStyle}>
                    <div className="loading-spinner" style={{ width: 32, height: 32, margin: '0 auto 12px' }} />
                    Đang tải...
                  </td>
                </tr>
              ) : accounts.length === 0 ? (
                <tr>
                  <td colSpan={showTtcMetrics ? 15 : 13} style={emptyCellStyle}>Chưa có tài khoản.</td>
                </tr>
              ) : accounts.map(account => {
                const selected = selectedIds.has(account.id);
                return (
                  <tr key={account.id} style={{ borderBottom: '1px solid #eef2f7', backgroundColor: selected ? '#eff6ff' : 'white' }}>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={selected} onChange={() => toggleSelectOne(account.id)} />
                    </td>
                    <td style={tdStyle}>#{account.id}</td>
                    <td style={tdStyle}><strong>{account.username}</strong></td>
                    <td style={tdStyle}>{groupOf(account) || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: account.coin > 0 ? '#047857' : '#6b7280' }}>{formatNumber(account.coin)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: account.coin_die > 0 ? '#b91c1c' : '#6b7280' }}>{formatNumber(account.coin_die)}</td>
                    {showTtcMetrics && <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(account.malucsub)}</td>}
                    {showTtcMetrics && <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(account.maluclikepage)}</td>}
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{renderYesNo(account.cookie)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{renderYesNo(account.access_token)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{renderStatusBadge(account.status)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{renderLiveBadge(account)}</td>
                    <td style={{ ...tdStyle, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={account.note || ''}>{account.note || '-'}</td>
                    <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDate(account.created)}</td>
                    <td style={{ ...tdStyle, minWidth: 250 }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <button style={smallButtonStyle} disabled={busy} onClick={() => runAccountAction('login', [account.id])}>Login</button>
                        <button style={smallButtonStyle} disabled={busy} onClick={() => runAccountAction('check-cookie', [account.id])}>Cookie</button>
                        <button style={smallButtonStyle} disabled={busy} onClick={() => runAccountAction('update-coin', [account.id])}>Xu</button>
                        <button style={smallDangerButtonStyle} disabled={busy} onClick={() => handleDeleteSingle(account.id, account.username)}>Xóa</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={paginationStyle}>
          <button style={secondaryButtonStyle} disabled={page === 1 || busy} onClick={() => setPage(current => Math.max(1, current - 1))}>Trang trước</button>
          <span style={{ color: '#4b5563', fontWeight: 600 }}>Trang {page} / {totalPages}</span>
          <button style={secondaryButtonStyle} disabled={page === totalPages || busy} onClick={() => setPage(current => Math.min(totalPages, current + 1))}>Trang sau</button>
        </div>
      </div>

      {showImportModal && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <div style={modalHeaderStyle}>
              <h2 style={{ margin: 0, fontSize: 22 }}>Import account {title}</h2>
              <button style={iconButtonStyle} onClick={() => setShowImportModal(false)} disabled={actionLoading === 'import'}>×</button>
            </div>
            <label style={labelStyle}>Danh sách tài khoản</label>
            <textarea
              value={importText}
              onChange={event => setImportText(event.target.value)}
              placeholder={'username|password\nusername|password|1000'}
              style={textareaStyle}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 12, alignItems: 'end', marginTop: 14 }}>
              <div>
                <label style={labelStyle}>Nhóm (tùy chọn)</label>
                <input value={importGroup} onChange={event => setImportGroup(event.target.value)} placeholder="VD: nhóm chạy chính" style={{ ...inputStyle, width: '100%' }} />
              </div>
              <button style={primaryButtonStyle} disabled={actionLoading === 'import'} onClick={handleImport}>
                {actionLoading === 'import' ? 'Đang import...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {actionProgress && (
        <ActionProgressPanel
          progress={actionProgress}
          now={clockTick}
          onCancel={cancelRunningAction}
          onClose={() => setActionProgress(null)}
        />
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={statCardStyle}>
    <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 8 }}>{label}</div>
    <div style={{ color: '#111827', fontSize: 28, fontWeight: 800 }}>{value}</div>
  </div>
);

const ActionProgressPanel: React.FC<{
  progress: ActionProgress;
  now: number;
  onCancel: () => void;
  onClose: () => void;
}> = ({ progress, now, onCancel, onClose }) => {
  const elapsedMs = (progress.active ? now : progress.finishedAt || now) - progress.startedAt;
  const percent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;

  return (
    <div style={progressPanelStyle}>
      <div style={progressHeaderStyle}>
        <div>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 2 }}>{progress.active ? actionConfig[progress.action].running : `${progress.label} hoàn tất`}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{progress.completed}/{progress.total} tài khoản</div>
        </div>
        {progress.active ? (
          <button style={smallDangerButtonStyle} onClick={onCancel}>Hủy</button>
        ) : (
          <button style={smallButtonStyle} onClick={onClose}>Đóng</button>
        )}
      </div>

      <div style={progressTrackStyle}>
        <div style={{ ...progressBarStyle, width: `${percent}%` }} />
      </div>

      <div style={progressMetaStyle}>
        <span>Thành công: <strong style={{ color: '#047857' }}>{progress.success}</strong></span>
        <span>Lỗi: <strong style={{ color: '#b91c1c' }}>{progress.failed}</strong></span>
        <span>Thời gian: <strong>{formatDuration(elapsedMs)}</strong></span>
      </div>

      {progress.active && (
        <div style={currentActionStyle}>
          Đang xử lý: #{progress.currentId || '-'}{progress.currentUsername ? ` - ${progress.currentUsername}` : ''}
          <div style={{ marginTop: 4, color: '#64748b' }}>Mỗi account có timeout {Math.round(ACTION_REQUEST_TIMEOUT_MS / 1000)} giây. Nếu proxy/site kẹt, dòng log sẽ báo timeout.</div>
        </div>
      )}

      <div style={progressLogStyle}>
        {progress.logs.length === 0 ? (
          <div style={{ color: '#64748b' }}>Chưa có log.</div>
        ) : progress.logs.map((log, index) => {
          const colors = log.status === 'success'
            ? { bg: '#dcfce7', text: '#166534', label: 'OK' }
            : log.status === 'failed'
              ? { bg: '#fee2e2', text: '#991b1b', label: 'Lỗi' }
              : { bg: '#dbeafe', text: '#1d4ed8', label: 'Đang chạy' };
          const duration = log.finishedAt ? formatDuration(log.finishedAt - log.startedAt) : formatDuration(now - log.startedAt);
          return (
            <div key={`${log.id}-${index}-${log.startedAt}`} style={progressLogRowStyle}>
              <span style={{ ...badgeStyle, backgroundColor: colors.bg, color: colors.text }}>{colors.label}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#111827' }}>#{log.id}{log.username ? ` - ${log.username}` : ''} <span style={{ color: '#64748b', fontWeight: 500 }}>({duration})</span></div>
                <div style={progressMessageStyle} title={log.message}>{log.message}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const pageHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 22,
};

const statsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: 12,
  marginBottom: 18,
};

const statCardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 18,
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
  alignItems: 'center',
  marginBottom: 12,
};

const selectedBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  padding: 12,
  border: '1px solid #bfdbfe',
  borderRadius: 8,
  backgroundColor: '#eff6ff',
  marginBottom: 12,
};

const inputStyle: React.CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '10px 12px',
  fontSize: 14,
  backgroundColor: 'white',
  color: '#111827',
  boxSizing: 'border-box',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  width: '100%',
  minHeight: 220,
  resize: 'vertical',
  fontFamily: 'Consolas, monospace',
  lineHeight: 1.5,
};

const tableShellStyle: React.CSSProperties = {
  backgroundColor: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  overflow: 'hidden',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
};

const thStyle: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 800,
  color: '#4b5563',
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  verticalAlign: 'middle',
  color: '#1f2937',
};

const emptyCellStyle: React.CSSProperties = {
  padding: 44,
  textAlign: 'center',
  color: '#6b7280',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 24,
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const primaryButtonStyle: React.CSSProperties = {
  border: '1px solid #2563eb',
  borderRadius: 6,
  backgroundColor: '#2563eb',
  color: 'white',
  padding: '10px 16px',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: 6,
  backgroundColor: 'white',
  color: '#1f2937',
  padding: '10px 14px',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};

const dangerButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  borderColor: '#fecaca',
  color: '#b91c1c',
};

const smallButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  padding: '6px 9px',
  fontSize: 12,
};

const smallDangerButtonStyle: React.CSSProperties = {
  ...smallButtonStyle,
  borderColor: '#fecaca',
  color: '#b91c1c',
};

const paginationStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: 14,
  borderTop: '1px solid #e5e7eb',
  backgroundColor: '#f8fafc',
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  width: 'min(760px, 100%)',
  maxHeight: '90vh',
  overflowY: 'auto',
  backgroundColor: 'white',
  borderRadius: 8,
  padding: 22,
  boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
};

const modalHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  marginBottom: 18,
};

const iconButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: '#6b7280',
  fontSize: 28,
  lineHeight: 1,
  cursor: 'pointer',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 6,
  fontSize: 14,
  fontWeight: 700,
  color: '#374151',
};

const progressPanelStyle: React.CSSProperties = {
  position: 'fixed',
  right: 18,
  bottom: 18,
  width: 'min(560px, calc(100vw - 36px))',
  maxHeight: '70vh',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 14,
  borderRadius: 8,
  backgroundColor: 'white',
  border: '1px solid #d1d5db',
  boxShadow: '0 20px 48px rgba(15, 23, 42, 0.24)',
  zIndex: 900,
};

const progressHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  alignItems: 'flex-start',
};

const progressTrackStyle: React.CSSProperties = {
  height: 8,
  borderRadius: 999,
  backgroundColor: '#e5e7eb',
  overflow: 'hidden',
};

const progressBarStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: 999,
  backgroundColor: '#2563eb',
  transition: 'width 0.2s ease',
};

const progressMetaStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px 14px',
  color: '#4b5563',
  fontSize: 13,
};

const currentActionStyle: React.CSSProperties = {
  padding: 10,
  borderRadius: 6,
  backgroundColor: '#f8fafc',
  color: '#111827',
  fontSize: 13,
};

const progressLogStyle: React.CSSProperties = {
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingRight: 4,
};

const progressLogRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '8px 0',
  borderTop: '1px solid #eef2f7',
};

const progressMessageStyle: React.CSSProperties = {
  color: '#4b5563',
  fontSize: 12,
  lineHeight: 1.45,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export default ExchangeAccountManager;
